import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Clock, MapPin, Phone, Store } from 'lucide-react';
import { backendGet } from '../lib/backendClient';

function formatCurrency(value) {
  return `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;
}

function formatDate(value) {
  if (!value) return 'Nao informado';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Nao informado';
  return d.toLocaleDateString('pt-BR');
}

function timeOnPlatform(createdAt) {
  if (!createdAt) return 'Tempo nao informado';
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 'Tempo nao informado';
  const diffDays = Math.max(1, Math.floor((Date.now() - created.getTime()) / 86400000));
  if (diffDays < 30) return `${diffDays} dia${diffDays > 1 ? 's' : ''} na Correfood`;
  const months = Math.floor(diffDays / 30);
  if (months < 12) return `${months} mes${months > 1 ? 'es' : ''} na Correfood`;
  const years = Math.floor(months / 12);
  return `${years} ano${years > 1 ? 's' : ''} na Correfood`;
}
function formatAddress(address) {
  if (!address || typeof address !== 'string') return 'Endereco nao informado';
  const parts = address.split('|').map((item) => item.trim()).filter(Boolean);
  if (parts.length === 0) return 'Endereco nao informado';
  if (parts.length !== 6) return address;
  const [rua, bairro, numero, cep, cidade, referencia] = parts;
  return `Rua ${rua}, ${numero} - ${bairro}, ${cidade}${cep ? `, CEP ${cep}` : ''}${referencia ? ` (${referencia})` : ''}`;
}

export default function RestaurantInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function loadStore() {
      try {
        const data = await backendGet(`/api/restaurants/${id}`, { skipAuth: true });
        if (mounted) setStore(data || null);
      } catch (error) {
        console.error('Erro ao carregar informacoes da loja:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadStore();
    return () => {
      mounted = false;
    };
  }, [id]);

  const address = useMemo(() => {
    if (!store) return '';
    if (typeof store.address === 'string' && store.address.trim()) return formatAddress(store.address);
    if (typeof store.endereco === 'string' && store.endereco.trim()) return formatAddress(store.endereco);
    return 'Endereco nao informado';
  }, [store]);

  if (loading) {
    return (
      <div className="restaurant-info-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Carregando informacoes da loja...
      </div>
    );
  }

  if (!store) {
    return (
      <div className="restaurant-info-page" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', gap: 16 }}>
        <p>Loja nao encontrada.</p>
        <button
          className="restaurant-info-back-btn"
          type="button"
          onClick={() => navigate(-1)}
          style={{ border: 'none', background: '#FF6B00', color: '#fff', padding: '10px 14px', borderRadius: 8, cursor: 'pointer' }}
        >
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="restaurant-info-page" style={{ minHeight: '100vh', background: '#f4f5f7', padding: '20px' }}>
      <div className="restaurant-info-container" style={{ maxWidth: 980, margin: '0 auto' }}>
        <button
          className="restaurant-info-back-btn"
          type="button"
          onClick={() => navigate(-1)}
          style={{ border: 'none', background: 'transparent', color: '#FF6B00', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}
        >
          <ArrowLeft size={18} />
          Voltar
        </button>

        <div className="restaurant-info-card" style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
          <h1 className="restaurant-info-title" style={{ margin: 0, fontSize: 30, color: '#1f2937' }}>{store.nome || store.name || 'Loja'}</h1>
          <p className="restaurant-info-description" style={{ marginTop: 8, marginBottom: 18, color: '#6b7280' }}>{store.description || store.descricao || 'Sem descricao'}</p>

          <div className="restaurant-info-grid" style={{ display: 'grid', gap: 12 }}>
            <div className="restaurant-info-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#374151' }}>
              <Store size={16} color="#FF6B00" />
              <span>Categoria: {store.category || store.categoria || 'Nao informada'}</span>
            </div>
            <div className="restaurant-info-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#374151' }}>
              <MapPin size={16} color="#FF6B00" />
              <span>{address}</span>
            </div>
            <div className="restaurant-info-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#374151' }}>
              <Phone size={16} color="#FF6B00" />
              <span>{store.phone || store.telefone || 'Contato nao informado'}</span>
            </div>
            <div className="restaurant-info-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#374151' }}>
              <Clock size={16} color="#FF6B00" />
              <span>Entrega: {formatCurrency(store.delivery_fee)}</span>
            </div>
            <div className="restaurant-info-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#374151' }}>
              <CalendarDays size={16} color="#FF6B00" />
              <span>Desde {formatDate(store.created_at)} ({timeOnPlatform(store.created_at)})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

